import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { timeApi } from '../../../api/time.js';
import { hrApi } from '../../../api/hr.js';

/**
 * Timesheets — create a timesheet for an employee/period, browse an employee's
 * timesheets, and (for time managers) work an approval queue of submitted
 * timesheets with approve/reject per row.
 */
export default function TimesheetsPage() {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [items, setItems] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  function loadQueue() {
    timeApi
      .listTimesheets({ status: 'submitted' })
      .then((r) => setQueue(r?.items || []))
      .catch(() => {}); // managers only — silent for self-service employees
  }

  useEffect(() => {
    setLoading(true);
    hrApi
      .listEmployees()
      .catch(() => ({ items: [] }))
      .then((e) => setEmployees(e?.items || e || []))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
    loadQueue();
  }, []);

  async function loadTimesheets(empId) {
    if (!empId) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const r = await timeApi.listTimesheets({ employee: empId });
      setItems(r?.items || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  }

  function onSelectEmployee(id) {
    setEmployeeId(id);
    setNotice(null);
    loadTimesheets(id);
  }

  async function createTimesheet(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const empId = fd.get('employeeId');
    if (!empId) {
      setError('Select an employee first');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { timesheet } = await timeApi.createTimesheet({
        employeeId: empId,
        periodStart: fd.get('periodStart'),
        periodEnd: fd.get('periodEnd'),
      });
      setNotice(`Timesheet created for ${String(timesheet.period_start).slice(0, 10)}`);
      if (empId === employeeId) loadTimesheets(empId);
      else onSelectEmployee(empId);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create timesheet');
    } finally {
      setBusy(false);
    }
  }

  async function decide(id, action) {
    setBusy(true);
    try {
      if (action === 'approve') await timeApi.approveTimesheet(id, {});
      else await timeApi.rejectTimesheet(id, {});
      loadQueue();
      if (employeeId) loadTimesheets(employeeId);
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${action}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-testid="time-timesheets-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Timesheets</h1>
          <p className="muted">Build, submit, and approve weekly timesheets</p>
        </div>
      </header>

      {error && <div className="alert error" data-testid="time-timesheets-error">{error}</div>}
      {notice && <div className="alert info" data-testid="time-timesheets-notice">{notice}</div>}

      <form onSubmit={createTimesheet} className="card form-card grid-form">
        <label>Employee
          <select name="employeeId" data-testid="time-timesheet-employee-input" value={employeeId} onChange={(e) => onSelectEmployee(e.target.value)}>
            <option value="">— select —</option>
            {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.user_name || emp.employee_code || emp.id}</option>)}
          </select>
        </label>
        <label>Period start<input name="periodStart" type="date" data-testid="time-timesheet-start-input" required /></label>
        <label>Period end<input name="periodEnd" type="date" data-testid="time-timesheet-end-input" required /></label>
        <div className="full actions"><button className="btn primary" type="submit" data-testid="time-timesheet-create" disabled={busy}>Create timesheet</button></div>
      </form>

      <div className="card">
        <h3>Timesheets</h3>
        <table className="data-table" data-testid="time-timesheet-list">
          <thead><tr><th>Period</th><th>Status</th><th>Hours</th><th></th></tr></thead>
          <tbody>
            {items.map((ts) => (
              <tr key={ts.id} data-testid={`time-timesheet-row-${ts.id}`}>
                <td>{String(ts.period_start).slice(0, 10)} → {String(ts.period_end).slice(0, 10)}</td>
                <td data-testid={`time-timesheet-status-${ts.id}`}>{ts.status}</td>
                <td>{Number(ts.total_hours)}</td>
                <td><Link className="btn ghost" to={`/time/timesheets/${ts.id}`} data-testid={`time-timesheet-open-${ts.id}`}>Open</Link></td>
              </tr>
            ))}
            {!loading && employeeId && items.length === 0 && <tr><td colSpan={4} className="muted">No timesheets yet.</td></tr>}
            {!employeeId && <tr><td colSpan={4} className="muted">Select an employee to see timesheets.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3>Approval queue</h3>
        <table className="data-table" data-testid="time-approval-list">
          <thead><tr><th>Employee</th><th>Period</th><th>Hours</th><th>Actions</th></tr></thead>
          <tbody>
            {queue.map((ts) => (
              <tr key={ts.id} data-testid={`time-approval-row-${ts.id}`}>
                <td>{ts.employee_name || ts.employee_code || ts.employee_id}</td>
                <td>{String(ts.period_start).slice(0, 10)} → {String(ts.period_end).slice(0, 10)}</td>
                <td>{Number(ts.total_hours)}</td>
                <td>
                  <button className="btn primary" data-testid={`time-approve-${ts.id}`} onClick={() => decide(ts.id, 'approve')} disabled={busy}>Approve</button>
                  <button className="btn ghost" data-testid={`time-reject-${ts.id}`} onClick={() => decide(ts.id, 'reject')} disabled={busy}>Reject</button>
                </td>
              </tr>
            ))}
            {queue.length === 0 && <tr><td colSpan={4} className="muted">Nothing awaiting approval.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
